import { useState } from "react";
import { IoChevronBack } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import NotViewedStatusList from "./NotViewedStatusList";
import ViewedStatusList from "./ViewedStatusList";
import { useStatusList } from "../../../store/api/useStatusList";
import MyStatus from "./MyStatus/MyStatus";
import { useAppSelector } from "../../../utils/hooks";
import { formatRelativeTime } from "../../../utils/formatUTCtoLocalDate";
import { useTranslateText } from "../../../hooks/useTranslateText";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import TextTranslate from "../../../utils/TextTranslate";

const TABS = [
  { id: "not_viewed" as const, label: "Recent" },
  { id: "viewed" as const, label: "Viewed" },
];

const groupClass =
  "overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";

export default function StatusList() {
  const navigate = useNavigate();
  const translate = useTranslateText();
  const { translate: translateTime, hour12 } = useTimeFormatOptions();
  const [tab, setTab] = useState<"not_viewed" | "viewed">("not_viewed");
  const userData = useAppSelector((state) => state.userData);
  const { data } = useStatusList();

  return (
    <div className="relative flex h-[100dvh] w-full flex-col overflow-hidden bg-secondary pb-[calc(3.25rem+env(safe-area-inset-bottom))] shadow-inner lg:max-w-md lg:pb-0">
      <div className="sticky top-0 z-20 shrink-0 border-b border-black/[0.06] bg-secondary/85 backdrop-blur-xl dark:border-white/[0.08]">
        <div className="flex items-center justify-between px-3 py-3">
          <button
            type="button"
            aria-label="Back"
            className="grid h-9 w-9 place-content-center rounded-full bg-black/[0.06] text-darkText transition active:scale-95 dark:bg-white/10 lg:invisible"
            onClick={() => navigate(-1)}
          >
            <IoChevronBack className="text-xl" />
          </button>
          <span className="text-[17px] font-semibold tracking-tight text-darkText">
            <TextTranslate text="Status" />
          </span>
          <div className="h-9 w-9" aria-hidden />
        </div>

        <div className="px-4 pb-3">
          <div
            className="grid grid-cols-2 rounded-[9px] bg-black/[0.06] p-[3px] dark:bg-white/[0.1]"
            role="tablist"
          >
            {TABS.map((item) => {
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(item.id)}
                  className={`rounded-[7px] py-1.5 text-center text-[13px] font-semibold transition ${
                    active
                      ? "bg-primary text-darkText shadow-sm"
                      : "text-darkText/55"
                  }`}
                >
                  <TextTranslate text={item.label} />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-4">
        <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
          {userData.user_name}
        </div>
        <div className={`${groupClass} mb-5`}>
          <MyStatus
            name={translate("My Status")}
            time={
              data?.data.my_stories?.[0]?.story_id
                ? formatRelativeTime(data?.data.my_stories?.[0]?.updatedAt, {
                    translate: translateTime,
                    hour12,
                  })
                : translate("No Updates")
            }
            currentStatusId={data?.data.my_stories?.[0]?.story_id || 0}
          />
        </div>

        <div className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wide text-lightText">
          <TextTranslate text={tab === "not_viewed" ? "Recent" : "Viewed"} />
        </div>
        <div className={groupClass}>
          {tab === "not_viewed" ? (
            <NotViewedStatusList />
          ) : (
            <ViewedStatusList />
          )}
        </div>
      </div>
    </div>
  );
}
